import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { useUpdateProject } from "@/hooks/useProjects";
import { UpdateProjectPayload } from "@/lib/projects/api";
import { ProjectSummary } from "@/types";
import { Settings } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type ProjectSettingsDialogProps = {
  project: ProjectSummary | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export function ProjectSettingsDialog({
  project,
  open,
  onOpenChange,
}: ProjectSettingsDialogProps) {
  const { toast } = useToast();
  const updateProjectMutation = useUpdateProject();

  const [name, setName] = useState("");
  const [rootDirectory, setRootDirectory] = useState("");
  const [deployType, setDeployType] = useState("");
  const [port, setPort] = useState("");
  const [buildCommand, setBuildCommand] = useState("");
  const [installCommand, setInstallCommand] = useState("");
  const [buildOutDir, setBuildOutDir] = useState("");
  const [autoDeployEnabled, setAutoDeployEnabled] = useState(false);

  const config = project?.config;
  const canEdit = !!project;

  useEffect(() => {
    if (!project) {
      return;
    }
    setName(project.name ?? "");
    setRootDirectory(config?.rootDirectory ?? "");
    setDeployType(config?.deployType ?? "");
    setPort(config?.port ? String(config.port) : "");
    setBuildCommand(config?.buildCommand ?? "");
    setInstallCommand(config?.installCommand ?? "");
    setBuildOutDir(config?.buildOutDir ?? "");
    setAutoDeployEnabled(config?.autoDeployEnabled ?? false);
  }, [project, config]);

  const deployTypeOptions = useMemo(
    () => [
      { value: "SHARED_COMPUTE_INSTANCE", label: "Server app" },
      { value: "CDN_SITE", label: "Static site" },
    ],
    [],
  );

  const handleSave = async () => {
    if (!project) {
      return;
    }
    if (name.trim() === "") {
      toast({
        title: "Project name required",
        description: "Please provide a project name before saving.",
        variant: "destructive",
      });
      return;
    }
    const payload: UpdateProjectPayload = {
      name: name.trim(),
      rootDirectory: rootDirectory.trim(),
      buildCommand: buildCommand.trim(),
      installCommand: installCommand.trim(),
      buildOutDir: buildOutDir.trim(),
      autoDeployEnabled,
    };

    if (deployType) {
      payload.deployType = deployType;
    }

    const isServerDeploy = deployType === "SHARED_COMPUTE_INSTANCE";
    if (port.trim() === "") {
      if (isServerDeploy) {
        toast({
          title: "Port required",
          description: "Please provide a port for server apps.",
          variant: "destructive",
        });
        return;
      }
    } else {
      const parsed = Number(port);
      if (Number.isNaN(parsed) || parsed <= 0) {
        toast({
          title: "Invalid port",
          description: "Port must be a positive number.",
          variant: "destructive",
        });
        return;
      }
      payload.port = parsed;
    }

    try {
      await updateProjectMutation.mutateAsync({
        id: project.id,
        payload,
      });
      toast({
        title: "Project updated",
        description: "New settings will apply to future deploys.",
      });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: "Update failed",
        description:
          error?.response?.data?.message ??
          "Unable to update project settings.",
        variant: "destructive",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-slate-900 border-cyan-500/30">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-cyan-400">
            <Settings className="h-5 w-5" />
            Project configuration
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Updates apply to future deploys. Existing deploys stay unchanged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="project-name" className="text-cyan-300">
              Project name
            </Label>
            <Input
              id="project-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              className="bg-slate-800/50 border-cyan-500/30 text-cyan-100 focus:border-cyan-400"
              disabled={!canEdit}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="root-directory" className="text-cyan-300">
                Root directory
              </Label>
              <Input
                id="root-directory"
                placeholder="Leave blank for repo root"
                value={rootDirectory}
                onChange={(event) => setRootDirectory(event.target.value)}
                className="bg-slate-800/50 border-cyan-500/30 text-cyan-100 focus:border-cyan-400"
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-cyan-300">Deploy type</Label>
              <Select
                value={deployType}
                onValueChange={setDeployType}
                disabled={!canEdit}
              >
                <SelectTrigger className="bg-slate-800/50 border-cyan-500/30 text-cyan-100 focus:border-cyan-400">
                  <SelectValue placeholder="Select deploy type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-cyan-500/30">
                  {deployTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="port" className="text-cyan-300">
                Port{" "}
                {deployType === "SHARED_COMPUTE_INSTANCE"
                  ? "(required)"
                  : "(optional)"}
              </Label>
              <Input
                id="port"
                type="number"
                min={1}
                value={port}
                onChange={(event) => setPort(event.target.value)}
                className="bg-slate-800/50 border-cyan-500/30 text-cyan-100 focus:border-cyan-400"
                disabled={!canEdit || deployType === "CDN_SITE"}
                required={deployType === "SHARED_COMPUTE_INSTANCE"}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="build-out-dir" className="text-cyan-300">
                Build output directory
              </Label>
              <Input
                id="build-out-dir"
                value={buildOutDir}
                onChange={(event) => setBuildOutDir(event.target.value)}
                className="bg-slate-800/50 border-cyan-500/30 text-cyan-100 focus:border-cyan-400"
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className="rounded-lg border border-cyan-500/30 bg-slate-800/50 p-4 flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm font-medium text-cyan-200">
                Auto-deploy on push
              </p>
              <p className="text-xs text-slate-400">
                Use this as the default for new deploys on this project.
              </p>
            </div>
            <Switch
              checked={autoDeployEnabled}
              onCheckedChange={setAutoDeployEnabled}
              className="data-[state=checked]:bg-cyan-500"
              disabled={!canEdit}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="build-command" className="text-cyan-300">
                Build command
              </Label>
              <Input
                id="build-command"
                value={buildCommand}
                onChange={(event) => setBuildCommand(event.target.value)}
                className="bg-slate-800/50 border-cyan-500/30 text-cyan-100 focus:border-cyan-400"
                disabled={!canEdit}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="install-command" className="text-cyan-300">
                Install command
              </Label>
              <Input
                id="install-command"
                value={installCommand}
                onChange={(event) => setInstallCommand(event.target.value)}
                className="bg-slate-800/50 border-cyan-500/30 text-cyan-100 focus:border-cyan-400"
                disabled={!canEdit}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="text-slate-300 hover:text-white"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canEdit || updateProjectMutation.isPending}
              className="cyber-button"
            >
              {updateProjectMutation.isPending ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
